/**
 * Reveal — thin wrapper around useReveal that applies the global `.reveal`
 * class plus `.is-revealed` once the element crosses the viewport threshold.
 *
 * Renders nothing extra: just adds `ref` + class + optional transitionDelay
 * to the chosen tag. Use for site-wide scroll-driven section discovery.
 *
 * `prefers-reduced-motion` is handled by CSS in global.css (the .reveal class
 * neutralizes opacity/transform/transition under reduced-motion). No JS branch
 * is needed here — the IntersectionObserver still fires (cheap), it just
 * doesn't visually animate.
 */
import type { CSSProperties, ReactNode } from 'react';
import { useReveal } from './use-reveal';

type RevealTag = 'div' | 'section' | 'header';

export function Reveal({
  as,
  delay,
  threshold = 0.15,
  className,
  children,
  ...rest
}: {
  as?: RevealTag;
  delay?: number;
  threshold?: number;
  className?: string;
  children: ReactNode;
} & Record<string, unknown>) {
  const [ref, revealed] = useReveal<HTMLElement>(threshold);
  const Tag = (as ?? 'div') as RevealTag;
  const cls = ['reveal', revealed ? 'is-revealed' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  const style: CSSProperties | undefined =
    delay && delay > 0 ? { transitionDelay: `${delay}ms` } : undefined;

  // We pass ref via `as any` because React doesn't infer a single ref type for
  // the polymorphic `as` prop; consumers don't see this.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const RefTag = Tag as any;
  return (
    <RefTag ref={ref} className={cls} style={style} {...rest}>
      {children}
    </RefTag>
  );
}
